import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';
import { useSelector } from 'react-redux';

import { CircularProgress, Theme, WithStyles } from '@material-ui/core';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import { createStyles, withStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { Star } from '@material-ui/icons';
import DomainIcon from '@material-ui/icons/Domain';
import { Button, UserAvatar } from 'core';
import TimeZoneSelector from 'core/AvailabilitySelector/TimeZoneSelector';
import { ShowOnAvailabilitiesListEnum, TAppointmentBookedTimeZoneStorage } from 'core/AvailabilitySelector/types';
import { allTimeZonesDetails, getAppointmentBookedTimeZoneStorageKey } from 'core/AvailabilitySelector/utils';
import { find, get, map } from 'lodash';
import { useHasAccessToConfirmDialogAppointment } from 'modules/Availability/shared/hooks';
import moment from 'moment';
import { TAvailability, TFacility, TProvider } from 'ui-model';
import { AddressService } from 'ui-utils';

import { EntityID } from '@lib/resources/types';
import { selectCurrentMaster, selectCurrentUser } from '@lib/selectors';
import storage from '@lib/services/storageService';

import { SvgIconWrapper } from 'components/v2';

import BiographyProvider from './BiographyProvider';

const styles = ({ spacing: { unit }, breakpoints: { down, up }, palette: { divider } }: Theme) =>
  createStyles({
    providerLink: {
      overflow: 'hidden',
    },
    providerText: {
      fontSize: 16,
      fontWeight: 500,
      lineHeight: '18px',
    },
    biography: {
      fontSize: 14,
      lineHeight: '18px',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      whiteSpace: 'nowrap',
    },
    biographyReadMore: {
      fontSize: 14,
      lineHeight: '18px',
    },
    facilityText: {
      flex: 'none',
      paddingLeft: 0,
      marginRight: '30px',
      maxWidth: 'fit-content',
    },
    providers: {
      borderTop: `1px solid ${divider}`,
      padding: unit,
      marginBottom: unit * 2,
    },
    provider: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      marginLeft: unit * 5,
      [down('sm')]: {
        flexDirection: 'column',
        marginLeft: 0,
        marginBottom: unit,
      },
    },
    providerAvatar: {
      [down('sm')]: {
        display: 'none',
      },
    },
    providerAvatarMobile: {
      [up('md')]: {
        display: 'none',
      },
    },
    providerInfo: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: `${unit}px`,
    },
    providerName: {
      display: 'flex',
      flexDirection: 'column',
      marginLeft: unit * 1.5,
      width: '30%',
      [down('sm')]: {
        marginLeft: 0,
        padding: `0px ${unit}px`,
        width: '100%',
        marginBottom: `${unit}px`,
      },
    },
    buttonPCP: {
      display: 'inline-flex',
      pointerEvents: 'none',
      fontSize: 11,
      fontWeight: 600,
      padding: '0 6px',
      borderRadius: 4,
      marginLeft: unit,
      '& svg': {
        fontSize: 11,
        marginRight: unit / 2,
      },
    },
    availabilities: {
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
      justifyContent: 'flex-start',
      padding: '0px 8px',
      maxWidth: '70%',
      [down('sm')]: {
        maxWidth: '100%',
        width: '100%',
        padding: 0,
      },
    },
    button: {
      minWidth: 76,
      minHeight: 32,
      margin: (unit * 1) / 2,
    },
  });

const messages = defineMessages({
  badgePCP: {
    id: 'components.v2.CardFacilityAvailabilities.badgePCP',
    defaultMessage: 'Your PCP',
  },
  showOnTimeZoneOption: {
    id: 'components.v2.CardFacilityAvailabilities.showOnTimeZoneOption',
    defaultMessage: 'All times are based on {timeZoneName} ({timeZoneAbbreviation})',
  },
});

export interface CardFacilityAvailabilitiesProps
  extends WithStyles<typeof styles>,
    Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  key: number | string;
  isMobile: boolean;
  groupByFacility: {
    facility: TFacility;
    group: Array<{
      provider: TProvider;
      availabilities: TAvailability[];
    }>;
  };
  primaryCareProvider?: EntityID;
  onAvailabilityChange: (availability: TAvailability) => void;
  facilities?: Array<TFacility>;
}

const localTimeZoneIANA = Intl.DateTimeFormat().resolvedOptions().timeZone || moment.tz.guess();

const CardFacilityAvailabilities: React.FC<CardFacilityAvailabilitiesProps> = ({
  classes,
  key,
  isMobile,
  groupByFacility,
  primaryCareProvider,
  onAvailabilityChange,
  facilities,
}) => {
  const { formatMessage } = useIntl();
  const pathname = window.location.pathname;
  const currentMaster = useSelector(selectCurrentMaster); // Master Account
  const currentUser = useSelector(selectCurrentUser); // Widget User
  const hasAccessConfirmDialogAppointment = useHasAccessToConfirmDialogAppointment();
  // Try getting the settings from master or widget user.
  const {
    shouldHideProviderDetails,
    initialCurrentTimeZoneSetting,
    useTimezoneFrom,
    customerTimeZoneIANA,
    displayPrimaryCareProviders,
  } = useMemo(
    () => ({
      shouldHideProviderDetails: get(
        currentMaster,
        'settings.scheduler.hideProviderDetails',
        get(currentUser, 'settings.scheduler.hideProviderDetails', false),
      ),
      initialCurrentTimeZoneSetting: get(
        currentMaster,
        'settings.scheduler.timezone.showOnAvailabilitiesList',
        get(
          currentUser,
          'settings.scheduler.timezone.showOnAvailabilitiesList',
          ShowOnAvailabilitiesListEnum.DoNotShow,
        ),
      ),
      useTimezoneFrom: get(currentMaster, 'settings.useTimezoneFrom', get(currentUser, 'settings.useTimezoneFrom', '')),
      customerTimeZoneIANA: get(currentMaster, 'settings.timezone', get(currentUser, 'settings.timezone', '')),
      displayPrimaryCareProviders: get(
        currentMaster,
        'settings.scheduler.displayPrimaryCareProviders',
        get(currentUser, 'settings.scheduler.displayPrimaryCareProviders', true),
      ),
    }),
    [currentMaster, currentUser],
  );

  const [availabilityIdSelected, setAvailabilityIdSelected] = useState('');
  const [currentTimeZoneSettng, setTimezoneSetting] = useState<ShowOnAvailabilitiesListEnum>(
    initialCurrentTimeZoneSetting,
  );

  useEffect(() => {
    setTimezoneSetting(initialCurrentTimeZoneSetting);
  }, [initialCurrentTimeZoneSetting]);

  const handleTimeZoneSetting = (newTimeZone: ShowOnAvailabilitiesListEnum) => {
    setTimezoneSetting(newTimeZone);
  };

  const customerOrFacilityTimeZoneIANA = useMemo(() => {
    if (useTimezoneFrom === 'user') {
      return customerTimeZoneIANA;
    }

    const currentFacilityId = get(groupByFacility, 'facility._id', '');
    const currentFacility = find(facilities, (facility) => facility._id === currentFacilityId);
    const currentFacilityTimeZoneIANA = get(currentFacility, 'settings.timezone', '');

    return currentFacilityTimeZoneIANA || customerTimeZoneIANA;
  }, [groupByFacility, facilities, customerTimeZoneIANA, useTimezoneFrom]);

  const { localTimeZoneDetails, customerOrFacilityTimeZoneDetails } = useMemo(() => {
    const localTimeZoneName = new Date()
      .toLocaleDateString('en-US', {
        timeZone: localTimeZoneIANA,
        timeZoneName: 'long',
        day: '2-digit',
      })
      .slice(4);

    const customerOrFacilityTimeZoneName = new Date()
      .toLocaleDateString('en-US', {
        timeZone: customerOrFacilityTimeZoneIANA,
        timeZoneName: 'long',
        day: '2-digit',
      })
      .slice(4);

    return {
      localTimeZoneDetails: allTimeZonesDetails.filter((timeZone) => timeZone.name === localTimeZoneName)[0],
      customerOrFacilityTimeZoneDetails: allTimeZonesDetails.filter(
        (timeZone) => timeZone.name === customerOrFacilityTimeZoneName,
      )[0],
    };
  }, [customerOrFacilityTimeZoneIANA]);

  const timeZonesOptions = [
    {
      name: formatMessage(messages.showOnTimeZoneOption, {
        timeZoneName: get(customerOrFacilityTimeZoneDetails, 'name', ''),
        timeZoneAbbreviation: get(customerOrFacilityTimeZoneDetails, 'abbreviation', ''),
      }),
      value: ShowOnAvailabilitiesListEnum.ShowRootOrFacility,
    },
    {
      name: formatMessage(messages.showOnTimeZoneOption, {
        timeZoneName: get(localTimeZoneDetails, 'alias', get(localTimeZoneDetails, 'name', '')),
        timeZoneAbbreviation: get(localTimeZoneDetails, 'abbreviation', ''),
      }),
      value: ShowOnAvailabilitiesListEnum.ShowPatient,
    },
  ];

  const getTimeZoneIANAToUse = () => {
    switch (currentTimeZoneSettng) {
      case ShowOnAvailabilitiesListEnum.ShowRootOrFacility:
        return customerOrFacilityTimeZoneIANA;
      case ShowOnAvailabilitiesListEnum.ShowPatient:
        return localTimeZoneIANA;
      default:
        return localTimeZoneIANA;
    }
  };

  return (
    <Paper key={key} elevation={1} square>
      <ListItem>
        <ListItemIcon>
          <SvgIconWrapper Icon={DomainIcon} color="textPrimary" fontSize="small" />
        </ListItemIcon>
        <ListItemText
          className={classes.facilityText}
          primary={<Typography variant="h6">{groupByFacility.facility.name}</Typography>}
          secondary={<Typography variant="caption">{AddressService.toString(groupByFacility.facility)}</Typography>}
        />
        {!isMobile && currentTimeZoneSettng !== ShowOnAvailabilitiesListEnum.DoNotShow && (
          <TimeZoneSelector
            timeZonesOptions={timeZonesOptions}
            currentTimeZoneSettng={currentTimeZoneSettng}
            onTimeZoneChange={handleTimeZoneSetting}
          />
        )}
      </ListItem>
      <div className={classes.providers}>
        {map(groupByFacility.group, (groupByProvider, index) => {
          const { provider } = groupByProvider;
          const isPrimary = primaryCareProvider === provider._id;
          // Get wrapper component for name and bio, if theres a bio render
          // as a link tag, if not render a div.
          const { WrapperComponent, props } =
            provider.biographyUrl && !shouldHideProviderDetails
              ? ({
                  WrapperComponent: 'a',
                  props: {
                    className: classes.providerLink,
                    href: provider.biographyUrl,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  },
                } as const)
              : ({ WrapperComponent: React.Fragment, props: {} } as const);

          return (
            <div key={`${index}-${provider._id}`} className={classes.provider}>
              <UserAvatar
                className={classes.providerAvatar}
                user={shouldHideProviderDetails ? false : (provider as any)}
              />
              <div className={classes.providerName}>
                <div className={classes.providerInfo}>
                  <UserAvatar
                    className={classes.providerAvatarMobile}
                    user={shouldHideProviderDetails ? false : (provider as any)}
                  />
                  <WrapperComponent {...props}>
                    <Typography className={classes.providerText} variant="body1">
                      {shouldHideProviderDetails ? `Provider ${index + 1}` : provider.name}
                      {isPrimary && displayPrimaryCareProviders && (
                        <Button
                          className={classes.buttonPCP}
                          icon={<Star fontSize="small" />}
                          variant="contained"
                          color="secondary"
                          size="small"
                        >
                          {formatMessage(messages.badgePCP)}
                        </Button>
                      )}
                    </Typography>
                  </WrapperComponent>
                </div>
                {provider.biography && !shouldHideProviderDetails && <BiographyProvider provider={provider} />}
              </div>
              <div className={classes.availabilities}>
                {map(groupByProvider.availabilities, (availability) => {
                  const isSelectedAvailability = availabilityIdSelected === availability._id;
                  // WebScheduler doesnt handle the DialogConfirmAppt rollout yet, so it will lock the slot when you click on it. so we should show the loading on the webScheduler
                  const shouldShowLoading =
                    (isSelectedAvailability && /\/survey/.test(pathname)) ||
                    (!hasAccessConfirmDialogAppointment && isSelectedAvailability);
                  return (
                    <Button
                      key={availability._id}
                      className={classes.button}
                      variant="contained"
                      color="primary"
                      size="small"
                      disabled={shouldShowLoading}
                      onClick={() => {
                        setAvailabilityIdSelected(availability._id);
                        onAvailabilityChange(availability);

                        const appointmentBookedTimeZoneToStorage: TAppointmentBookedTimeZoneStorage = {
                          setting: currentTimeZoneSettng,
                          IANA: getTimeZoneIANAToUse(),
                          abbreviation:
                            currentTimeZoneSettng === ShowOnAvailabilitiesListEnum.ShowRootOrFacility
                              ? get(customerOrFacilityTimeZoneDetails, 'abbreviation', '')
                              : get(localTimeZoneDetails, 'abbreviation', ''),
                        };
                        storage.set(
                          getAppointmentBookedTimeZoneStorageKey(availability._id),
                          appointmentBookedTimeZoneToStorage,
                        );
                      }}
                    >
                      {shouldShowLoading ? (
                        <CircularProgress size={16} />
                      ) : (
                        moment(availability.date)
                          .tz(getTimeZoneIANAToUse())
                          .format('LT')
                          .toLowerCase()
                          .replace(' ', '')
                      )}
                    </Button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Paper>
  );
};

export default withStyles(styles)(CardFacilityAvailabilities);
